if (!customElements.get('klaim-community-stream')) {
  class KlaimCommunityStream extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.viewport = this.querySelector('[data-community-viewport]');
      this.stage = this.querySelector('.khcr__stage');
      this.track = this.querySelector('[data-community-track]');
      this.progressBar = this.querySelector('[data-community-progress]');
      this.progressFill = this.querySelector('[data-community-progress-fill]');
      this.items = [...this.querySelectorAll('.khcr__item')];
      this.media = this.items.map(item => item.querySelector('video')).filter(Boolean);
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      if (!this.viewport || !this.track || !this.items.length) return;

      this.items.forEach(item => item.querySelectorAll('img, video').forEach(media => { media.draggable = false; }));
      this.bindScroll();
      this.onDragStart = event => event.preventDefault();
      this.addEventListener('dragstart', this.onDragStart, true);
      this.onContextMenu = event => {
        if (event.target.closest('img, video')) event.preventDefault();
      };
      this.addEventListener('contextmenu', this.onContextMenu);
      this.onTouchStart = event => {
        if (!event.target.closest?.('.khcr__item') || !event.changedTouches.length) return;
        this.activeTouchId = event.changedTouches[0].identifier;
      };
      this.onTouchMove = event => {
        if (this.activeTouchId === undefined || !event.cancelable) return;
        if ([...event.touches].some(touch => touch.identifier === this.activeTouchId)) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      };
      this.onTouchEnd = event => {
        if ([...event.changedTouches].some(touch => touch.identifier === this.activeTouchId)) this.activeTouchId = undefined;
      };
      this.addEventListener('touchstart', this.onTouchStart, { capture: true, passive: true });
      this.addEventListener('touchmove', this.onTouchMove, { capture: true, passive: false });
      this.addEventListener('touchend', this.onTouchEnd, { capture: true, passive: true });
      this.addEventListener('touchcancel', this.onTouchEnd, { capture: true, passive: true });
      this.observeVideos();
      this.resizeObserver = new ResizeObserver(() => {
        this.measure();
        this.render();
      });
      this.resizeObserver.observe(this);
      this.measure();
      this.render();
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      this.videoObserver?.disconnect();
      window.clearTimeout(this.snapTimer);
      this.removeEventListener('dragstart', this.onDragStart, true);
      this.removeEventListener('contextmenu', this.onContextMenu);
      this.removeEventListener('touchstart', this.onTouchStart, true);
      this.removeEventListener('touchmove', this.onTouchMove, true);
      this.removeEventListener('touchend', this.onTouchEnd, true);
      this.removeEventListener('touchcancel', this.onTouchEnd, true);
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      if (this.lenis && window.klaimHomeLenis === this.lenis) {
        this.lenis.destroy();
        window.klaimHomeLenis = null;
      }
    }

    bindScroll() {
      this.onScroll = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = 0;
          this.render();
        });
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onResize = () => {
        this.measure();
        this.onScroll();
      };
      window.addEventListener('resize', this.onResize, { passive: true });
      if (this.dataset.smoothScroll === 'true' && window.matchMedia('(min-width: 750px)').matches && !this.reducedMotion.matches) this.loadLenis();
    }

    loadLenis() {
      const start = () => {
        if (!this.isConnected || !window.Lenis || window.klaimHomeLenis) return;
        this.lenis = new window.Lenis({ autoRaf: true, anchors: true, smoothWheel: true, syncTouch: false });
        window.klaimHomeLenis = this.lenis;
      };
      if (window.Lenis) {
        start();
        return;
      }
      if (window.klaimLenisLoading) {
        window.klaimLenisLoading.then(start);
        return;
      }
      window.klaimLenisLoading = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://unpkg.com/lenis@1.3.26/dist/lenis.min.js';
        script.onload = resolve;
        script.onerror = reject;
        document.head.append(script);
      });
      window.klaimLenisLoading.then(start).catch(() => {});
    }

    measure() {
      this.pageTop = window.scrollY + this.viewport.getBoundingClientRect().top;
      this.travel = Math.max(1, this.viewport.offsetHeight - window.innerHeight);
    }

    render() {
      const progress = Math.min(1, Math.max(0, (window.scrollY - this.pageTop) / this.travel));
      const position = progress * Math.max(0, this.items.length - 1);
      const focus = Math.round(position);
      const center = window.innerWidth / 2;
      const lower = Math.floor(position);
      const upper = Math.min(this.items.length - 1, lower + 1);
      const lowerCenter = this.items[lower].offsetLeft + this.items[lower].offsetWidth / 2;
      const upperCenter = this.items[upper].offsetLeft + this.items[upper].offsetWidth / 2;
      const focusCenter = lowerCenter + (upperCenter - lowerCenter) * (position - lower);
      const offset = center - focusCenter;

      this.track.style.transform = `translate3d(${offset}px, 0, 0)`;
      this.items.forEach((item, index) => {
        const distance = Math.abs(index - position);
        const scale = Math.max(0.5, 1.14 - distance * 0.32);
        const scaleValue = scale.toFixed(3);
        if (item.style.getPropertyValue('--scale') !== scaleValue) item.style.setProperty('--scale', scaleValue);
        const isFocused = index === focus;
        if ((item.getAttribute('aria-current') === 'true') !== isFocused) item.setAttribute('aria-current', String(isFocused));
        if (item.matches('a')) {
          const tabIndex = isFocused ? 0 : -1;
          if (item.tabIndex !== tabIndex) item.tabIndex = tabIndex;
        }
      });
      this.style.setProperty('--community-progress', progress.toFixed(4));
      if (this.progressBar) {
        this.progressBar.setAttribute('aria-valuenow', (progress * (this.items.length - 1)).toFixed(1));
        this.progressBar.setAttribute('aria-valuetext', `Photo ${focus + 1} of ${this.items.length}`);
      }
      if (this.progressFill) this.progressFill.style.transform = `scaleX(${Math.max(progress, 0.04)})`;
      if (focus !== this.lastFocus) {
        this.media.forEach(video => {
          const shouldPlay = video.closest('.khcr__item') === this.items[focus] && !this.reducedMotion.matches;
          if (shouldPlay && video.paused) video.play().catch(() => {});
          else if (!shouldPlay && !video.paused) video.pause();
        });
        this.lastFocus = focus;
      }
    }

    observeVideos() {
      this.media.forEach(video => {
        video.muted = true;
        video.loop = true;
        video.playsInline = true;
      });
    }
  }

  customElements.define('klaim-community-stream', KlaimCommunityStream);
}
